import React, { FC, useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { message } from 'antd';
import Axios from 'axios';
import Seo from 'app/components/Seo';
import Layout from 'app/layouts/layout';
import HorizontalLine from 'app/components/HorizontalLine';
import Banner from 'app/components/Banner/FullWidth';
import Container from 'app/layouts/layout/Container';
import Typography from 'app/components/Typography';
import SkeletonComp from 'app/pages/Article/Skeleton';
import { TYPOGRAPHY } from 'app/components/Typography/models.d';
import { getTemplate, TemplatePageI } from 'app/adapters/TemplatePages';
import { sanitizedContent } from 'app/utils/sanitizedContent';
import useRecentlyLinks from 'app/hooks/useRecentlyLinks';
import { SeoI } from 'app/@types/seo.d';
import './Template.scss';

export const TemplatePage: FC = () => {
  let history = useHistory();
  let { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<TemplatePageI[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [seoData, setSeoData] = useState<SeoI | null>(null);
  const { updateRecentlyLinks } = useRecentlyLinks();

  const getTemplatePagesData = useCallback(async () => {
    try {
      const { data } = await getTemplate({ slug__iexact: slug });

      !data.results.length && history.push('/page-not-found');
      setSeoData(data.results[0]?.seo);
      setPage(data.results);
      setIsLoading(false);
    } catch (error) {
      if (!Axios.isCancel(error)) {
        message.error('An error occurred while fetching the page data');
      }
    }
  }, [history, slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
    getTemplatePagesData();
  }, [getTemplatePagesData]);

  useEffect(() => {
    if (page.length) {
      updateRecentlyLinks(page[0].page_title, `${page[0].slug}`);
    }
  }, [page, updateRecentlyLinks]);

  return (
    <Layout>
      {seoData && (
        <Seo
          title={seoData.meta_title}
          description={seoData.meta_description}
          {...seoData}
        />
      )}
      {!isLoading ? (
        <>
          {page[0].image ? (
            <Banner
              bannerImg={page[0].image}
              img_alt={page[0].img_alt ?? ''}
              bannerTitle={page[0].image_title}
              titleColor={
                page[0].image_title_color ? page[0].image_title_color : '#fff'
              }
            />
          ) : null}
          <Container className="template-page">
            {page[0].page_title && (
              <Typography
                variant={TYPOGRAPHY.VARIANT.H1}
                className="template-page__title"
              >
                {page[0].page_title}
              </Typography>
            )}
            <div className="template-page__body">
              {page[0].content && (
                <div
                  dangerouslySetInnerHTML={sanitizedContent(page[0].content)}
                />
              )}
            </div>
            <HorizontalLine />
          </Container>
        </>
      ) : (
        <Container>
          <SkeletonComp />
        </Container>
      )}
    </Layout>
  );
};
